import { Range } from 'react-range';
import { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { confirmAlert } from '../../../utils/alerts';
import {
  ServiceActions,
  ServiceCard,
  ServiceDivider,
  ServiceField,
  ServiceHeader,
  ServicePrimaryButton,
  ServiceSliderRow,
  ServiceTitleBlock,
} from './ServiceComponent.styles';
import { BrokerageFieldRow, BrokerageNotice } from './BrokeraggioV2.styles';

const STEP = 1;
const MIN = 0;
const ISSUANCE_FEE_THRESHOLD = 20;
const ISSUANCE_FEE_AMOUNT = 10;

function BrokeraggioComponent({
  rc_quotation, sendBrokerageAmount, getTotal,
}) {
  const [val, setVal] = useState({ values: [0] });
  const valRef = useRef(null);
  valRef.current = val;

  useEffect(() => {
    if (rc_quotation) {
      setVal({ values: [rc_quotation.brokerage_amount] });
    }
  }, [rc_quotation]);

  const brokerageBase = getTotal()
    - ((Number(rc_quotation?.issuance_fee) || 0) / (rc_quotation?.payment_frequency || 1));

  const issuanceFeeAmount = rc_quotation?.issuance_fee_override === null
    || rc_quotation?.issuance_fee_override === undefined
    ? ISSUANCE_FEE_AMOUNT
    : Number(rc_quotation.issuance_fee_override);

  const handleAddBrokerageClick = () => {
    if (valRef.current.values[0] >= ISSUANCE_FEE_THRESHOLD) {
      confirmAlert(
        `Hai selezionato € ${valRef.current.values[0]} di brokeraggio. `
        + `Verranno aggiunti €${issuanceFeeAmount} di spese di emissione a carico del cliente: `
        + `la RCA aumenta di €${issuanceFeeAmount}.`,
        () => sendBrokerageAmount(valRef.current.values[0]),
        () => { },
        'Spese di emissione',
      );
      return;
    }

    sendBrokerageAmount(valRef.current.values[0]);
  };

  const maxBrokerage = (brokerageBase * 30) / 100;

  const handleAddClick = rc_quotation.product_id === 1
    ? () => confirmAlert(
      'Sei sicuro di voler aggiungere il brokeraggio? Se lo aggiungi, non potrai più aggiungere un servizio.',
      () => handleAddBrokerageClick(),
    )
    : () => handleAddBrokerageClick();

  const handleInputChange = (e) => {
    const maxVal = (brokerageBase * 30) / 100;
    if (e.target.value > maxVal) {
      setVal({
        values: [parseInt(maxVal)],
      });
    } else {
      setVal({
        values: [
          Math.floor(e.target.value * 100)
          / 100,
        ],
      });
    }
  };

  const handleRangeChange = (values) => {
    const maxAllowed = (brokerageBase * 30) / 100;
    const amount = values[0] + 0.5;
    if (maxAllowed < amount) {
      setVal({
        values: [
          Math.floor(maxAllowed * 100)
          / 100,
        ],
      });
    } else {
      setVal({
        values: [
          Math.floor(values * 100) / 100,
        ],
      });
    }
  };

  return (
    <ServiceCard>
      <ServiceHeader>
        <ServiceTitleBlock>
          <h3>Aggiungi Brokeraggio</h3>
          <p>(Se il titolo è semestrale allora il brokeraggio viene diviso in due rate)</p>
        </ServiceTitleBlock>
        <ServiceActions>
          <ServicePrimaryButton type="button" onClick={handleAddClick}>
            Aggiungi
          </ServicePrimaryButton>
        </ServiceActions>
      </ServiceHeader>
      <BrokerageNotice>
        <FontAwesomeIcon icon={faTriangleExclamation} />
        <p>
          Fai attenzione perché il brokeraggio non
          può essere modificato dopo che è stato
          effettuato un pagamento.
        </p>
      </BrokerageNotice>
      <ServiceDivider />
      <BrokerageFieldRow>
        <ServiceField>
          <label htmlFor="brokerage-value">Inserisci Brokeraggio</label>
          <input
            id="brokerage-value"
            type="number"
            placeholder="Inserisci il brokeraggio"
            value={val.values}
            max={maxBrokerage}
            onChange={handleInputChange}
          />
        </ServiceField>
        {val.values[0] >= ISSUANCE_FEE_THRESHOLD && (
          <BrokerageNotice>
            <FontAwesomeIcon icon={faTriangleExclamation} />
            <p>
              {`Superando i € ${ISSUANCE_FEE_THRESHOLD} di brokeraggio verranno aggiunti € ${issuanceFeeAmount} di spese di emissione a carico del cliente.`}
            </p>
          </BrokerageNotice>
        )}
      </BrokerageFieldRow>
      <ServiceSliderRow>
        <span>&euro; 0.00</span>
        <div className="slider">
          <Range
            values={val.values}
            step={STEP}
            min={MIN}
            max={maxBrokerage}
            onChange={handleRangeChange}
            renderTrack={({ props, children }) => (
              <div
                onMouseDown={props.onMouseDown}
                onTouchStart={props.onTouchStart}
                style={{
                  ...props.style,
                  height: '32px',
                  display: 'flex',
                  width: '95%',
                  margin: '0 auto',
                }}
              >
                <div
                  ref={props.ref}
                  style={{
                    height: '8px',
                    width: '100%',
                    borderRadius: '140px',
                    alignSelf: 'center',
                    backgroundColor: '#DFF3EA',
                  }}
                >
                  {children}
                </div>
              </div>
            )}
            renderThumb={({ props }) => (
              <div
                {...props}
                style={{
                  ...props.style,
                  height: '24px',
                  width: '24px',
                  borderRadius: '50%',
                  backgroundColor: '#00A651',
                  border: '3px solid #fff',
                  boxShadow: '0 1px 4px rgba(16, 24, 40, 0.25)',
                  outline: 'none',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: '-28px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    whiteSpace: 'nowrap',
                    color: '#00A651',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: '15px',
                    fontWeight: 600,
                    lineHeight: '20px',
                  }}
                >
                  &euro;
                  {' '}
                  {Math.floor(val.values[0] * 100)
                    / 100}
                </div>
              </div>
            )}
          />
        </div>
        <span>
          &euro;
          {' '}
          {maxBrokerage.toFixed(2)}
        </span>
      </ServiceSliderRow>
    </ServiceCard>
  );
}

export default BrokeraggioComponent;
